import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  FormControlLabel,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import csgfReportingService from '../../services/csgfReportingService';
import { buildMonthOptions } from '../../utils/reportingPeriods';
import ProductUsageTable from './ProductUsageTable';

// Columns of the Partnership Update grid, in workbook order
const SEGMENT_COLUMNS = [
  { key: 'eligible', label: 'Total Eligible', caption: (p) => `as of ${p.anchor_date}` },
  { key: 'engaged', label: 'Engaged', caption: (p) => `active ${p.engaged_start} – ${p.anchor_date}` },
  { key: 'all_active', label: 'All Active', caption: (p) => `since ${p.active_start}` },
  {
    key: 'active_networks',
    label: 'Active Networks',
    caption: (p) => `1+ user with ≥2 logins since ${p.active_start}`,
  },
];

const POPULATION_ROWS = [
  { key: 'users', label: 'Users', entity: 'users' },
  { key: 'networks', label: 'Networks', entity: 'networks' },
  { key: 'schools', label: 'Schools', entity: 'schools' },
];

const CSGFReporting = ({ month, onMonthChange }) => {
  const { enqueueSnackbar } = useSnackbar();
  const monthOptions = useMemo(buildMonthOptions, []);
  const [excludeStaff, setExcludeStaff] = useState(true);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(null);

  // Drill-down drawer
  const [drawer, setDrawer] = useState(null); // { segment, entity, label }
  const [detailRows, setDetailRows] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailSearch, setDetailSearch] = useState('');

  const loadReport = useCallback(async (refresh = false) => {
    setLoading(true);
    try {
      const data = await csgfReportingService.getReport({ month, refresh, excludeStaff });
      setReport(data);
      if (refresh) enqueueSnackbar('Report recalculated', { variant: 'success' });
    } catch (error) {
      console.error('CSGF report error:', error);
      enqueueSnackbar(error.message || 'Failed to load the CSGF report', { variant: 'error' });
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [month, excludeStaff, enqueueSnackbar]);

  useEffect(() => { loadReport(false); }, [loadReport]);

  const openDetails = useCallback(async (segment, entity, label) => {
    setDrawer({ segment, entity, label });
    setDetailSearch('');
    setDetailLoading(true);
    try {
      const data = await csgfReportingService.getDetails(segment, entity, { month, excludeStaff });
      setDetailRows(data.rows || []);
    } catch (error) {
      console.error('CSGF detail error:', error);
      enqueueSnackbar(error.message || 'Failed to load the supporting records', { variant: 'error' });
      setDetailRows([]);
    } finally {
      setDetailLoading(false);
    }
  }, [month, excludeStaff, enqueueSnackbar]);

  const download = useCallback(async (kind) => {
    setDownloading(kind);
    try {
      if (kind === 'report') {
        await csgfReportingService.downloadReport({ month, excludeStaff });
      } else if (kind === 'rawUsers') {
        await csgfReportingService.downloadRawUsers({ month });
      } else if (kind === 'details' && drawer) {
        await csgfReportingService.downloadDetails(drawer.segment, drawer.entity, { month, excludeStaff });
      }
    } catch (error) {
      console.error('CSGF download error:', error);
      enqueueSnackbar(error.message || 'Download failed', { variant: 'error' });
    } finally {
      setDownloading(null);
    }
  }, [month, excludeStaff, drawer, enqueueSnackbar]);

  const period = report?.period;

  const filteredDetailRows = useMemo(() => {
    if (!detailSearch.trim()) return detailRows;
    const query = detailSearch.toLowerCase();
    return detailRows.filter((row) =>
      Object.values(row).some((value) => String(value).toLowerCase().includes(query))
    );
  }, [detailRows, detailSearch]);

  const detailColumns = filteredDetailRows.length ? Object.keys(filteredDetailRows[0]) : [];

  return (
    <>
      {/* ── Controls ─────────────────────────────────────────────────────── */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
          <Select
            size="small"
            value={month}
            onChange={(e) => onMonthChange(e.target.value)}
            sx={{ minWidth: 220 }}
          >
            {monthOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}{option.inProgress ? ' (in progress)' : ''}
              </MenuItem>
            ))}
          </Select>

          <FormControlLabel
            control={
              <Switch
                checked={excludeStaff}
                onChange={(e) => setExcludeStaff(e.target.checked)}
                size="small"
              />
            }
            label="Exclude CSGF staff"
          />

          <Button
            startIcon={<RefreshIcon />}
            onClick={() => loadReport(true)}
            disabled={loading}
            variant="outlined"
          >
            Recalculate
          </Button>

          <Box sx={{ flexGrow: 1 }} />

          <Button
            startIcon={downloading === 'report' ? <CircularProgress size={16} /> : <DownloadIcon />}
            onClick={() => download('report')}
            disabled={!report || !!downloading}
            variant="contained"
          >
            Export report (xlsx)
          </Button>
          <Button
            startIcon={downloading === 'rawUsers' ? <CircularProgress size={16} /> : <DownloadIcon />}
            onClick={() => download('rawUsers')}
            disabled={!!downloading}
            variant="outlined"
          >
            Download raw users (csv)
          </Button>
        </Stack>

        {period && (
          <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
            <Chip size="small" label={`Reporting period: ${period.period_start} – ${period.period_end}`} />
            <Chip size="small" label={`Last Updated / anchor: ${period.anchor_date}`} color="primary" />
            <Chip size="small" label={`Rolling 90 days: ${period.engaged_start} – ${period.anchor_date}`} />
            <Chip size="small" label={`All Active since: ${period.active_start}`} />
            {report?.from_snapshot && (
              <Tooltip title="Returned from the saved snapshot for this month. Recalculate to recompute from live data.">
                <Chip size="small" label="Saved snapshot" variant="outlined" />
              </Tooltip>
            )}
          </Stack>
        )}
      </Paper>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {report?.warnings?.map((warning) => (
        <Alert key={warning} severity="info" sx={{ mb: 2 }}>{warning}</Alert>
      ))}

      {/* ── Portfolio Key Engagement Metrics ─────────────────────────────── */}
      {report && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" gutterBottom>Portfolio Key Engagement Metrics</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Click any number to see the records behind it.
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Population</TableCell>
                  {SEGMENT_COLUMNS.map((column) => (
                    <TableCell key={column.key} align="center" sx={{ fontWeight: 600 }}>
                      {column.label}
                      <Typography variant="caption" display="block" color="text.secondary">
                        {column.caption(period)}
                      </Typography>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {POPULATION_ROWS.map((row) => (
                  <TableRow key={row.key} hover>
                    <TableCell sx={{ fontWeight: 500 }}>{row.label}</TableCell>
                    {SEGMENT_COLUMNS.map((column) => {
                      const value = report.metrics[column.key]?.[row.key];
                      if (value === null || value === undefined) {
                        return <TableCell key={column.key} align="center">n/a</TableCell>;
                      }
                      return (
                        <TableCell key={column.key} align="center">
                          <Button
                            size="small"
                            onClick={() => openDetails(
                              column.key, row.entity, `${column.label} — ${row.label}`
                            )}
                            sx={{ fontSize: '1.05rem', fontWeight: 600, minWidth: 64 }}
                          >
                            {value.toLocaleString()}
                          </Button>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Divider sx={{ my: 2 }} />
          <Button
            size="small"
            onClick={() => openDetails('eligible', 'relationships', 'All user–network–school relationships')}
          >
            View user / network / school relationships
          </Button>
        </Paper>
      )}

      {/* ── Product Usage ────────────────────────────────────────────────── */}
      {report && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>Product Usage</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {report.usage_tracking?.tracking_start
              ? `CSGF users who used each tool, across the current month, a rolling 90-day window, `
                + `and all recorded time. Tracking began ${report.usage_tracking.tracking_start}.`
              : 'No usage events recorded yet.'}
          </Typography>
          <ProductUsageTable
            productUsage={report.product_usage}
            period={period}
            usageTracking={report.usage_tracking}
            showNetworks
          />
        </Paper>
      )}

      {/* ── Drill-down ───────────────────────────────────────────────────── */}
      <Drawer
        anchor="right"
        open={!!drawer}
        onClose={() => setDrawer(null)}
        PaperProps={{ sx: { width: { xs: '100%', md: '80%' }, p: 2 } }}
      >
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            {drawer?.label}
            {!detailLoading && (
              <Typography component="span" color="text.secondary" sx={{ ml: 1 }}>
                ({filteredDetailRows.length.toLocaleString()} records)
              </Typography>
            )}
          </Typography>
          <Button
            size="small"
            startIcon={downloading === 'details' ? <CircularProgress size={16} /> : <DownloadIcon />}
            onClick={() => download('details')}
            disabled={!detailRows.length || !!downloading}
          >
            Export CSV
          </Button>
          <IconButton onClick={() => setDrawer(null)}><CloseIcon /></IconButton>
        </Stack>

        <TextField
          size="small"
          fullWidth
          placeholder="Filter records…"
          value={detailSearch}
          onChange={(e) => setDetailSearch(e.target.value)}
          sx={{ mb: 2 }}
        />

        {detailLoading ? (
          <LinearProgress />
        ) : (
          <TableContainer sx={{ maxHeight: '75vh' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {detailColumns.map((column) => (
                    <TableCell key={column} sx={{ fontWeight: 600 }}>{column}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredDetailRows.slice(0, 1000).map((row, index) => (
                  <TableRow key={index} hover>
                    {detailColumns.map((column) => (
                      <TableCell key={column}>{String(row[column] ?? '')}</TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {filteredDetailRows.length > 1000 && (
              <Typography variant="caption" color="text.secondary" sx={{ p: 1, display: 'block' }}>
                Showing the first 1,000 of {filteredDetailRows.length.toLocaleString()} records.
                Export the CSV for the full set.
              </Typography>
            )}
          </TableContainer>
        )}
      </Drawer>
    </>
  );
};

export default CSGFReporting;
